import Rmbx from "../../util"
import { addSourceDataRow, bulkUpdateAndHandleResponse } from "../../dashboard-data/actions"
import { getGroupingChangeHandlers, getTimekeepingStatusChangeHandlers, processCellCallback, processHeaderCallback } from "./utils"
import { iRmbxColDef } from "../../components/custom-dashboards/settings-files/types"
import { tButtonClickHandler, tButtonClickHandlerFactory, tButtonClickHandlerTuple } from "../types"
import { performDataRefresh } from "../../actions/server-side-row-model"
import { IconAddCollaborator } from "@rhumbix/rmbx_design_system_web"
import { setShowEmployeeMode, toggleFancySearchVisible } from "../../actions/search-bar"
import { Column, ColumnApi, GridApi } from "ag-grid-community"
import { getV4Resources } from "../../api"
import { CustomRateType, iCurrentUser, RateType } from "../../common/types"
import { referenceableDataSelector } from "../../selectors"
import { exportRatesToCsv, parseCsvToRates, getColumnsForRateType } from "../../forms/RateTableForm/csv-utils"
import { tEntitySelection } from "../../cached-data/types"
import { openAddCohortEmployeesModal, openAddRowsToWeeklyTKModal, openAddEditWorkShiftModal } from "../../components/modals/actions"
import { bulkAddSourceData } from "../../dashboard-data/actions/write"
import { logUserAmplitudeEvent, WEEKLY_TIMEKEEPING_MODAL_ADD_COST_CODES_OPENED, WEEKLY_TIMEKEEPING_MODAL_ADD_EMPLOYEES_OPENED } from "../../common/amplitude-event-logging"
import { togglePlaceholdersVisible } from "../../actions"
import { toggleSearchBarState } from "../../actions"
import { getFlagEnabled } from "../../getFlagValue"
import { getWorkShifts } from "../../common/ag-grid-utils"
import { isNumber } from "../../common/validators"
import { tResourceObject } from "../../dashboard-data/types"
import { ReduxState, tResourceName } from "../../common/types"
import { updateSortOrder } from "../../api/patch"
import { convertCicoToShiftAndBreak, getObjectId } from "../../common/ag-grid-ts-utils"
import { convertURLParamsToObject, getCookie } from "../../common/ts-utils"
import { SHOW_EMPLOYEE_MODE, SHOW_EMPLOYEE_MODE_LABELS, urlForResource } from "../../common/constants"
import { getFilterQueryParams } from "../../filters/utils"
import { fetchLazyLoadResources } from "../../dashboard-data/actions/read"

export const addNewRow: tButtonClickHandler = (e, { columnApi, context, gridApi, args }) => {
    const cApi = columnApi as ColumnApi
    const initialRowData: Record<string, unknown> = args?.extraArgs?.initialRowData ?? {}
    if (context.updateSourceDataCb)
        bulkAddSourceData(
            context.settings.resources[0],
            cApi.getAllColumns()?.map(column => column.getColDef()) as iRmbxColDef[],
            [initialRowData],
            context.settings.otherSettings.rowLevelValidators,
            context.settings.otherSettings.hiddenColumnDefaults,
            context.groupKeyInfo,
            context.filters,
            context.updateSourceDataCb,
            context
        )
    else {
        context.dispatch(
            addSourceDataRow(
                context.settings.resources[0],
                cApi.getAllColumns()?.map(column => column.getColDef()) as iRmbxColDef[],
                context.settings.otherSettings.rowLevelValidators,
                context.settings.otherSettings.hiddenColumnDefaults,
                context.groupKeyInfo,
                context.filters,
                context
            )
        )
    }
    // If this is a user-sorted table and we've added a row, let's update the sorting on everything else.
    if (context.settings.otherSettings?.rowDragField) {
        const resourceName = context.settings.resources[0]
        const nodeData: tResourceObject[] = []
        gridApi.forEachNode(n => {
            if (n.id)
                nodeData.push({
                    ...n.data,
                    [context.settings.otherSettings?.rowDragField]: n.rowIndex,
                    modified: true,
                })
        })
        // Only if the resource name is in the urlForResource object, can we request to bulk update the data
        if ((resourceName in urlForResource && !context.settings.otherSettings?.suppressAutoSync) || !getFlagEnabled("WA-9079-wage-rates")) {
            context.dispatch(bulkUpdateAndHandleResponse(updateSortOrder, resourceName, nodeData, undefined, undefined, e => e.id, context.settings.otherSettings?.rowDragField))
        }
    }
    if (context.settings.gridSettings?.rowModelType === "serverSide") context.dispatch(performDataRefresh(true))
}

// Opens a modal where the user selects a set of workers to add to a table, specifically
//  the Timekeeping Entries tab on the Weekly Timekeeping modal
export const addEmployeeRows: tButtonClickHandler = (e, { columnApi, context }) => {
    logUserAmplitudeEvent(WEEKLY_TIMEKEEPING_MODAL_ADD_EMPLOYEES_OPENED, {})
    const colDefs = (columnApi.getAllColumns() || []).map(column => column.getColDef())

    context.dispatch(openAddRowsToWeeklyTKModal("employees", "employee", context, colDefs))
}

// Opens a modal where the user selects a set of cost codes to add to a table, specifically
// the Timekeeping Entries tab on the Weekly Timekeeping modal
export const addCostCodeRows: tButtonClickHandler = (e, { columnApi, context }) => {
    logUserAmplitudeEvent(WEEKLY_TIMEKEEPING_MODAL_ADD_COST_CODES_OPENED, {})
    const colDefs = (columnApi.getAllColumns() || []).map((column: Column) => column.getColDef())

    context.dispatch(openAddRowsToWeeklyTKModal("costCodes", "cost code", context, colDefs))
}

// Opens a modal where the user selects a set of employees to add to a cohort
export const addCohortEmployeeRows: tButtonClickHandler = (e, { columnApi, context, args }) => {
    const colDefs = (columnApi.getAllColumns() || []).map(column => column.getColDef())
    context.dispatch(openAddCohortEmployeesModal(args?.value, context, colDefs))
}

export const openFieldFormCreate: tButtonClickHandler = (e, params) => {
    const { context } = params
    // Opens a New Field Form in the right rail -
    const projectIds = Array.isArray(context.filters.projectId) ? context.filters.projectId : [context.filters.projectId]

    // Due to some particularities in the ListView setup with regard to Bundling, we need to check for the
    // "schema_names" attribute and pick the first one
    const schemaName = context.settings.additionalQueryParams.schema_name
        ? context.settings.additionalQueryParams.schema_name
        : context.settings.additionalQueryParams.schema_names
        ? context.settings.additionalQueryParams.schema_names[0]
        : ""

    const folderPath = context.settings.useNavFolderPaths ? context.settings.listViewTitle : null
    if (projectIds.length === 1) {
        context.sideRailContext.enableSideRail({
            flow: "FIELD_FORMS",
            folderPath,
            isNew: true,
            schemaName,
            projectId: projectIds[0],
            mode: "Create",
            extraBtnParams: getFlagEnabled("WA-7649-web-transforms") ? params : undefined,
            listViewFields: context.settings.otherSettings.requestSpecificFields,
            listViewContext: context.settings,
            gridId: context.selectedRows[0]?.gridId,
            enableSideRail: context.sideRailContext.enableSideRail,
        })
    }
}

export const openFieldFormSchemaCreate: tButtonClickHandler = (e, params) => {
    const { context } = params
    context.sideRailContext.enableSideRail({
        flow: "SCHEMA_EDITOR",
        schemaId: undefined,
    })
}

export const getGroupingChangeHandler: tButtonClickHandlerFactory = params => {
    const { currentGrouping } = params
    return [[`${currentGrouping} ▾`, getGroupingChangeHandlers(params), "group"]]
}

export const getExportHandlers: tButtonClickHandlerFactory = params => {
    /**
     * - Export every visible column that has a header name (which will exclude
     * things like checkbox and button columns).
     * - Any columns with the excludeFromExport set to "true" will be excluded from the export
     * - If exportAll is true, include all the columns (except the group column).
     * Note that these are only valid for Client Side Row Model
     */
    const exportAll = params.context.settings.otherSettings?.exportAll
    const gridApi = params.gridApi as GridApi
    const allColumns = params.columnApi.getAllGridColumns() as Column[]
    const columns =
        allColumns?.filter(column => {
            const { groupColumn, headerName, excludeFromExport } = column.getColDef() as iRmbxColDef
            if (!groupColumn) return exportAll || (!!headerName && !excludeFromExport)
        }) ?? []

    const resource = params.context.settings.resources[0]
    const rowModelType = params.context.settings.gridSettings?.rowModelType

    const exportParams = {
        columnKeys: columns,
        processCellCallback,
        processHeaderCallback,
        skipGroups: true, // Don't insert an empty row between groups
    }
    let exportToCsv
    if (rowModelType == "serverSide") {
        const headers = columns.map(column => {
            const { headerName } = column.getColDef() as iRmbxColDef
            return `"${headerName}"`
        })
        exportToCsv = async () => {
            // Include additionalQueryParams in the export API call to ensure all necessary data is fetched
            const additionalParams = params.context.settings.additionalQueryParams || {}

            let response
            if (getFlagEnabled("WA-9050-SSRM-export-csv-filters")) {
                // Get filter query params from context to respect right rail filters
                const filterQueryParams = getFilterQueryParams(params.context.filters, params.context.settings)
                response = await getV4Resources(resource, { ...additionalParams, ...filterQueryParams })
            } else {
                response = await getV4Resources(resource, additionalParams)
            }
            const resources = response.results

            while (response.next) {
                const nextParams = convertURLParamsToObject(response.next)
                response = await getV4Resources(resource, { ...additionalParams, ...nextParams })
                resources.push(...response.results)
            }

            const resourceRows = resources.map((resource: { [x: string]: any }) => {
                return columns.map(column => {
                    const colDef = column.getColDef()
                    const valueFormatter = colDef.valueFormatter as (params: any) => any
                    const valueGetter = colDef.valueGetter as (params: any) => any

                    if (getFlagEnabled("WA-8877-project-csv-cico-settings")) {
                        // Use the provided valueGetter. They exist for all column definitions
                        const value = valueGetter({ data: resource, colDef, context: params.context })
                        const isMultiValueField = value && ((Array.isArray(value) && value.length === 0) || (value[0]?.id !== undefined && value[0]?.name !== undefined))

                        if (isMultiValueField) {
                            const names = value.map((item: { name: string }) => item.name.replace(/"/g, '""').replace(/;/g, "\\;"))
                            return `"${names.join(";")}"`
                        }
                        if (valueFormatter && (value != undefined || colDef.cellRendererParams?.computedField)) {
                            return `"${valueFormatter({ value, data: resource })}"`
                        }
                        return value !== undefined && value !== null ? `"${value}"` : ""
                    } else {
                        try {
                            const field = colDef.field as string
                            // remove the / that precedes name
                            const formattedColumnName = field.substring(1)
                            // create into a dictionary in order to use the existing formatters
                            const property = { value: resource[formattedColumnName], data: resource }

                            const parts = formattedColumnName.split("/")
                            const isMultiValueField =
                                parts.length === 1 &&
                                property.value &&
                                ((Array.isArray(property.value) && property.value.length === 0) || (property.value[0]?.id !== undefined && property.value[0]?.name !== undefined))

                            if (parts.length === 2) {
                                // If the field is like custom_fields/fieldname, we need to access
                                // resource["custom_fields"]["fieldname"] to get the value of the field.
                                // Currently only strings from custom text fields are nested objects like this,
                                // so no need for stringifying or formatting at the moment.
                                return resource[parts[0]][parts[1]]
                            } else if (isMultiValueField) {
                                // Used to properly export array of choices fields without
                                // the extra whitespace added by multiChoiceValueFormatter
                                // and also support escaping values properly so that the exported
                                // CSV is not broken if one of the values contained a double quote
                                const names = property.value.map((item: { name: string }) => item.name.replace(/"/g, '""').replace(/;/g, "\\;"))
                                return `"${names.join(";")}"`
                            } else if (valueFormatter && resource[formattedColumnName] != undefined) {
                                return `"${valueFormatter(property)}"`
                            } else if (resource[formattedColumnName] != undefined) {
                                return `"${resource[formattedColumnName]}"`
                            } else if (colDef.cellRendererParams.computedField && valueFormatter) {
                                return `"${valueFormatter(property)}"`
                            } else {
                                return '""'
                            }
                        } catch (error) {
                            console.warn("Error formatting value for column", colDef.field, error)
                        }
                    }
                })
            })

            const headerRow = headers.join(",")
            const csvString = headerRow.concat("\n", resourceRows.map((resourceRow: any[]) => resourceRow.join(",")).join("\n"))
            // create a Blob object from a from the array of resources
            const csvData = new Blob([csvString], { type: "text/csv" })
            // create a DOMString which contains a URL representing the csvData blob object
            const csvUrl = URL.createObjectURL(csvData)
            // create an anchor element which when clicked downloads the csvData as 'export.csv'
            const a = document.createElement("a")
            a.href = csvUrl
            a.target = "_blank"
            a.download = "export.csv"

            document.body.appendChild(a)
            a.click()
        }
        return [["CSV", exportToCsv, "export"]]
    } else {
        exportToCsv = () => gridApi.exportDataAsCsv(exportParams)
        const exportToXls = () => gridApi.exportDataAsExcel(exportParams)

        return [
            ["CSV", exportToCsv, "export"],
            ["Excel", exportToXls, "export"],
        ]
    }
}

export const getRowToggleHandler: tButtonClickHandlerFactory = params => {
    const { gridApi, rowsExpanded, toggleRowExpansion } = params
    return [
        [
            `${rowsExpanded ? "Collapse" : "Expand"} All`,
            () => {
                if (rowsExpanded) {
                    gridApi.collapseAll()
                    toggleRowExpansion(false)
                } else {
                    gridApi.expandAll()
                    toggleRowExpansion(true)
                }
            },
            rowsExpanded ? "collapse" : "expand",
        ],
    ]
}

export const getTableTimekeepingStatusChangeHandlers: tButtonClickHandlerFactory = params => getTimekeepingStatusChangeHandlers(params, false)

export const getEmployeeModeChangeHandlers: tButtonClickHandlerFactory = (): tButtonClickHandlerTuple[] => {
    const showEmployeeMode = getCookie("show_employee_mode") ?? SHOW_EMPLOYEE_MODE.EMPLOYEES_WITH_TIME
    return Object.entries(SHOW_EMPLOYEE_MODE_LABELS)
        .filter(mode => mode[0] !== showEmployeeMode)
        .map(([key, value]) => {
            return [
                value,
                (e, { context }, store) => {
                    fetchLazyLoadResources(context, store?.getState() as ReduxState)
                    context.dispatch(setShowEmployeeMode(key))
                },
                "",
            ]
        })
}

export const openAddTimekeepingEntryModal: tButtonClickHandler = (e, { context }) => {
    context.openAddTimekeepingEntryModal()
}

export const openCreateTimeCardModal: tButtonClickHandler = (e, { context }) => {
    context.openCreateTimeCardModal()
}

export const navigateTo: tButtonClickHandler = (e, { args }) => {
    const isNotRelativeUrl = new RegExp("^(?:[a-z]+:)?//", "i")
    const url = args ? args.url || "" : ""

    if (!url.length || isNotRelativeUrl.test(url)) {
        return
    }
    Rmbx.util.history.push(url)
}

export const navigateToExternal: tButtonClickHandler = (e, { args }) => {
    const url = args ? args.url || "" : ""
    const isExternalURL = new URL(url).origin !== location.origin
    if (url.length && isExternalURL) {
        window.open(url, "_blank")
    }
    return
}

export const addProject: tButtonClickHandler = (_e, params) => params.sideRailContext.enableSideRail({ flow: "PROJECT_CREATE" })

export const addWorkRuleSet: tButtonClickHandler = (_e, params) => params.sideRailContext.enableSideRail({ flow: "WORK_RULE_SETS_CREATE" })

export const addCompanyHolidays: tButtonClickHandler = (_e, params) => params.sideRailContext.enableSideRail({ flow: "HOLIDAY_SETTINGS_CREATE" })

export const addEmployee: tButtonClickHandler = (_e, params) => {
    if (getFlagEnabled("WA-8927-certs-licenses-unions-enhancement")) {
        params.sideRailContext.enableSideRail({ flow: "EMPLOYEE_CREATE_WITH_TABS" })
    } else {
        params.sideRailContext.enableSideRail({ flow: "EMPLOYEE_CREATE" })
    }
}

export const addApiIntegrationToken: tButtonClickHandler = (_e, params) => params.sideRailContext.enableSideRail({ flow: "API_INTEGRATION_TOKEN_CREATE" })
/**
 * Launch the Collaborators view in the Side Rail.
 * @param {tMouseEvent<HTMLButtonElement> | null} e Click event.
 * @param context View context.
 */
export const openSideRailCollaborators: tButtonClickHandler = async (e, { context }) => {
    if (context.sideRailContext.sideRailEnabled) return
    const resource = "guestFormShares"

    // Grab the schema names in order to filter the form permissions
    const schemaNames = context.settings.additionalQueryParams.schema_names

    // This comes from modify production value getters
    context.sideRailContext.enableSideRail({
        flow: "DATA_TABLE",
        config: {
            schemaNames: schemaNames,
            useBasicHeader: true,
            hideApplyButton: true,
            cancelButtonText: "Done",
            headerIcon: IconAddCollaborator(),
        },
        filters: [],
        resource,
        title: `${context.settings.tableName} Collaborators`,
    })
}

export const toggleFancySearchBar: tButtonClickHandler = async (e, { context }) => {
    context.dispatch(toggleFancySearchVisible())
}

export const togglePlaceholders: tButtonClickHandler = async (e, { context }) => {
    context.dispatch(togglePlaceholdersVisible())
}

export const toggleStatefulButton: tButtonClickHandler = async (e, { context, args }) => {
    context.dispatch(toggleSearchBarState(args!.extraArgs!.stateAttribute))
}

export const editTimeCardDetails: tButtonClickHandler = async (e, { context, sourceData }) => {
    const workShiftId = Array.from(getWorkShifts(context, sourceData))[0] as number
    const resourceName = context.settings.resources[0] as tResourceName
    const rowToEdit = Object.values(sourceData[resourceName] as tResourceObject[]).find(e => {
        if (isNumber(e.work_shift_id) && e.work_shift_id == workShiftId) return true
        if (typeof e.work_shift_id === "object" && e.work_shift_id) if (e.work_shift_id.id === workShiftId || e.work_shift_id.tempId === workShiftId) return true
        return false
    })
    if (rowToEdit) context.dispatch(openAddEditWorkShiftModal(rowToEdit.work_shift_id, context))
}

export const openCicoDetails: tButtonClickHandler = (e, { context, sourceData, weeklyTkDetailContext }) => {
    // props.data gets stale if we have just created the row, so we need to find
    // the current row data in the state.
    const sourceObject = Object.values(sourceData)[0][0]
    const lockedColumn = context.groupKeyInfo.find(gki => gki.colDef.field === "/employee")
    if (sourceObject?.employee) {
        context.sideRailContext.enableSideRail({
            flow: "VIEW_CICO_DATA",
            lockedColumns: context.groupKeyInfo,
            employee: context.referenceableData.employees[getObjectId(sourceObject?.employee)],
            employeesInContext: weeklyTkDetailContext?.employeesInContext || [],
        })
    } else if (lockedColumn) {
        context.sideRailContext.enableSideRail({
            flow: "VIEW_CICO_DATA",
            lockedColumns: context.groupKeyInfo,
            employee: context.referenceableData.employees[getObjectId(lockedColumn.value)],
            employeesInContext: weeklyTkDetailContext?.employeesInContext || [],
        })
    }
}

export const openCsvExportForm: tButtonClickHandler = async (e, { context }) => {
    context.sideRailContext.enableSideRail({
        flow: "CREATE_CSV_EXPORT",
    })
}

/**
 * Opens the Copy Week modal for copying weekly time entries to another week.
 */
export const openCopyWeekModal: tButtonClickHandler = (e, { context }) => {
    context.openCopyWeekModal()
}

export const importShiftsAndBreaksFromCico: tButtonClickHandler = async (e, params) => {
    const { context, weeklyTkDetailContext } = params
    const localSourceData = weeklyTkDetailContext?.localSourceData || {}

    const tkEmployeeIds = localSourceData?.timekeepingEntries?.reduce((agg, row) => {
        if (!row.employee) return agg
        return agg.add(getObjectId(row.employee))
    }, new Set())
    const lockedColumnEmployee = context.groupKeyInfo.find(gki => gki.colDef.field === "/employee")?.value
    const lockedColumnTimecard = context.groupKeyInfo.find(gki => gki.colDef.field === "/work_shift_id")?.value
    const date = context.groupKeyInfo.find(gki => gki.colDef.field === "/date")!.value
    if (!lockedColumnEmployee && !tkEmployeeIds?.size) {
        return
    }
    getV4Resources("companyStartStopTypes", {
        for_dashboard: true,
    }).then(startStopTypes => {
        getV4Resources("timelineEntryVersions", {
            employee_id: tkEmployeeIds && tkEmployeeIds.size > 0 ? Array.from(tkEmployeeIds) : [getObjectId(lockedColumnEmployee)],
            shift_start_time_0: date,
            shift_start_time_1: date,
            for_dashboard: true,
        }).then(data => {
            const shiftsAndBreaks = convertCicoToShiftAndBreak(data.results, true, startStopTypes.results, true)
            if (lockedColumnTimecard) {
                shiftsAndBreaks.forEach(sb => {
                    sb.work_shift_id = lockedColumnTimecard
                    sb.isPlaceholder = false
                })
            } else {
                shiftsAndBreaks.forEach(sb => {
                    const tkEntry = localSourceData?.timekeepingEntries?.find(tk => getObjectId(tk.employee) === getObjectId(sb.employee))
                    if (tkEntry) {
                        sb.work_shift_id = tkEntry.work_shift_id
                        sb.isPlaceholder = false
                    }
                })
            }
            context.updateSourceDataCb!({ ewsStartStopTimes: shiftsAndBreaks }, false, undefined, undefined, undefined, true)
        })
    })
}

/**
 * Export the current rate table tab's rows to a CSV file.
 * Uses the navId from settings to determine the rate type, resolves IDs to display
 * names via referenceableData, and triggers a browser download.
 */
export const exportRateCsv: tButtonClickHandler = (e, { context, sourceData, args }, store) => {
    const rateType = (context.settings.navId ?? args?.extraArgs?.rateType) as RateType
    if (!rateType) return

    const customRateTypes = args?.extraArgs?.customRateTypes ?? []
    const rows = (sourceData as Record<string, Record<string, unknown>[]>)[rateType] ?? []
    const filename = `${context.settings.tableName ?? "rates"}.csv`

    // Get the latest referenceableData from the Redux store
    const currentState = store?.getState() as ReduxState | undefined
    const referenceableData = currentState?.entities ? referenceableDataSelector(currentState) : context.referenceableData

    exportRatesToCsv(rows, rateType, customRateTypes, context.currentUser, referenceableData, filename)
}

/**
 * Fetch all entities for the ID-based columns that the CSV import needs for
 * name-to-ID resolution. Merges the fetched data with the existing
 * referenceableData so the resolver has a complete lookup table.
 *
 * Parameters:
 * rateType (RateType): Which rate type's columns to inspect.
 * customRateTypes (CustomRateType[]): Custom rate type definitions.
 * currentUser (iCurrentUser): Current user context.
 * baseReferenceableData (tEntitySelection): Existing referenceableData to merge into.
 *
 * Returns:
 * Promise<{ data: tEntitySelection, warnings: string[] }>: referenceableData augmented
 *   with fetched entities, plus any warnings about failed fetches.
 */
export async function fetchReferenceableDataForImport(
    rateType: RateType,
    customRateTypes: CustomRateType[],
    currentUser: iCurrentUser,
    baseReferenceableData: tEntitySelection
): Promise<{ data: tEntitySelection; warnings: string[] }> {
    const columns = getColumnsForRateType(rateType, customRateTypes, currentUser)
    // Collect the unique resource names that need fetching (from idArray columns)
    const resourceNames = new Set<string>()
    columns.forEach(col => {
        if (col.kind === "idArray") {
            resourceNames.add(col.resourceName)
        }
    })

    // Fetch all entities for each resource and merge into referenceableData
    const augmented = { ...baseReferenceableData } as Record<string, Record<number, unknown>>
    const fetchWarnings: string[] = []
    const fetchPromises = Array.from(resourceNames).map(async resourceName => {
        try {
            let response = await getV4Resources(resourceName, {})
            let results = response.results ?? response
            // Follow pagination to fetch all pages
            while (response.next) {
                const nextParams = convertURLParamsToObject(response.next)
                response = await getV4Resources(resourceName, nextParams)
                const pageResults = response.results ?? response
                if (Array.isArray(pageResults)) {
                    results = [...(Array.isArray(results) ? results : []), ...pageResults]
                }
            }
            if (Array.isArray(results)) {
                const existing = (augmented[resourceName] ?? {}) as Record<number, unknown>
                const merged = { ...existing }
                results.forEach((item: Record<string, unknown>) => {
                    if (item.id != null) {
                        merged[item.id as number] = item
                    }
                })
                augmented[resourceName] = merged
            }
        } catch {
            fetchWarnings.push(`Failed to fetch ${resourceName} data. Some names may not resolve correctly.`)
        }
    })
    await Promise.all(fetchPromises)
    return { data: (augmented as unknown) as tEntitySelection, warnings: fetchWarnings }
}

/**
 * Import rates from a CSV file into the current rate table tab.
 * Creates a temporary file input, parses the selected CSV, and shows a
 * confirmation modal before replacing all existing rows on the tab.
 * Pre-fetches all ID-based referenceable entities to ensure name resolution works.
 */
export const importRateCsv: tButtonClickHandler = (e, { context, sourceData, args }, store) => {
    const rateType = (context.settings.navId ?? args?.extraArgs?.rateType) as RateType
    if (!rateType) return

    const customRateTypes = args?.extraArgs?.customRateTypes ?? []

    // Create a temporary file input to trigger the file picker
    const fileInput = document.createElement("input")
    fileInput.type = "file"
    fileInput.accept = ".csv,text/csv"
    fileInput.style.display = "none"

    const removeFileInput = () => {
        if (fileInput.parentNode) {
            fileInput.parentNode.removeChild(fileInput)
        }
    }

    fileInput.addEventListener("cancel", removeFileInput)

    fileInput.addEventListener("change", () => {
        const file = fileInput.files?.[0]
        if (!file) {
            removeFileInput()
            return
        }

        const reader = new FileReader()
        reader.onerror = () => {
            removeFileInput()
            context.createModalAction({
                title: "CSV Import Failed",
                description: "Failed to read the selected file.",
                confirmButtonText: "OK",
                isSingleButton: true,
                action: () => context.createModalAction(null),
                close: () => context.createModalAction(null),
            })
        }
        reader.onabort = () => {
            removeFileInput()
        }
        reader.onload = async (loadEvent: ProgressEvent<FileReader>) => {
            removeFileInput()
            const csvText = loadEvent.target?.result as string
            if (!csvText) return

            // Get the latest referenceableData from Redux, then augment it
            // by fetching all entities for ID-based columns
            const currentState = store?.getState() as ReduxState | undefined
            const baseRefData = currentState?.entities ? referenceableDataSelector(currentState) : context.referenceableData

            const { data: referenceableData, warnings: fetchWarnings } = await fetchReferenceableDataForImport(rateType, customRateTypes, context.currentUser, baseRefData)

            const { rows: importedRows, warnings: parseWarnings } = parseCsvToRates(csvText, rateType, customRateTypes, context.currentUser, referenceableData)

            const warnings = [...fetchWarnings, ...parseWarnings]

            if (importedRows.length === 0) {
                const errorMessage = warnings.length > 0 ? warnings.join("\n") : "No rows found in the CSV file."
                context.createModalAction({
                    title: "CSV Import Failed",
                    description: errorMessage,
                    confirmButtonText: "OK",
                    isSingleButton: true,
                    action: () => context.createModalAction(null),
                    close: () => context.createModalAction(null),
                })
                return
            }

            const existingCount = ((sourceData as Record<string, Record<string, unknown>[]>)[rateType] ?? []).length

            // Build confirmation message
            const replacedMsg = `${existingCount} existing row(s) will be replaced` + ` with ${importedRows.length} row(s) from the import.`
            const messageParts: string[] = [replacedMsg]
            if (warnings.length > 0) {
                messageParts.push("", "Warnings:", ...warnings)
            }

            context.createModalAction({
                title: "Rate Import Preview",
                description: messageParts.join("\n"),
                confirmButtonText: "Confirm",
                cancelButtonText: "Cancel",
                action: () => {
                    if (context.replaceSourceDataCb) {
                        const typedRows = importedRows.map(row => ({
                            ...row,
                            dataType: rateType,
                        }))
                        context.replaceSourceDataCb(rateType, typedRows)
                    }
                    context.createModalAction(null)
                },
                cancel: () => context.createModalAction(null),
                close: () => context.createModalAction(null),
            })
        }
        reader.readAsText(file)
    })

    document.body.appendChild(fileInput)
    fileInput.click()
}
